import type { Rng } from '../core/rng';
import { HOURS } from './config';
import { sortToppings } from './lingo';
import { TOPPINGS, type ItemKind, type OrderSpec, type Topping } from './types';

/** Combos people actually order, so tickets read like real Waffle House calls. */
export const FAVORITE_COMBOS: readonly (readonly Topping[])[] = [
  ['smothered', 'covered'],
  ['smothered', 'covered'],
  ['smothered', 'covered', 'chunked'],
  ['covered'],
  ['smothered'],
  ['covered', 'peppered'],
  ['covered', 'topped'],
  ['country'],
  ['covered', 'capped'],
  ['smothered', 'covered', 'peppered'],
  ['smothered', 'diced'],
  ['covered', 'chunked'],
  ['smothered', 'covered', 'topped'],
  ['covered', 'country'],
  ['smothered', 'covered', 'chunked', 'peppered'],
  ['smothered', 'covered', 'capped', 'diced'],
];

const TOPPING_POPULARITY: Record<Topping, number> = {
  smothered: 10,
  covered: 12,
  chunked: 6,
  diced: 4,
  peppered: 5,
  capped: 4,
  topped: 5,
  country: 5,
};

export interface OrderBias {
  /** Multiplies the chance of each item kind appearing. */
  kinds?: Partial<Record<ItemKind, number>>;
  /** Added to the hour's item count roll (can be negative). */
  extraItems?: number;
  /** Added to the hour's topping count roll. */
  extraToppings?: number;
  wellChance?: number;
}

export interface OrderContext {
  hour: number;
  /** Yellow index or a power outage: waffle irons are electric, the grill runs on gas. */
  limitedMenu: boolean;
  bias?: OrderBias;
}

const BASE_KIND_WEIGHT: Record<ItemKind, number> = {
  hashBrowns: 10,
  waffle: 6,
  eggs: 5,
  bacon: 4,
};

export function pickToppings(rng: Rng, count: number): Topping[] {
  if (count <= 0) return [];
  if (count >= TOPPINGS.length) return [...TOPPINGS];
  const combos = FAVORITE_COMBOS.filter((c) => c.length === count);
  if (combos.length > 0 && rng.chance(0.6)) return sortToppings(rng.pick(combos));
  const pool = [...TOPPINGS];
  const picked: Topping[] = [];
  while (picked.length < count && pool.length > 0) {
    const t = rng.weighted(pool.map((p) => [p, TOPPING_POPULARITY[p]] as const));
    picked.push(t);
    pool.splice(pool.indexOf(t), 1);
  }
  return sortToppings(picked);
}

export function generateOrder(rng: Rng, ctx: OrderContext): OrderSpec {
  const profile = HOURS[Math.max(0, Math.min(HOURS.length - 1, ctx.hour))] ?? HOURS[0]!;
  const bias = ctx.bias ?? {};

  const available: ItemKind[] = ctx.limitedMenu
    ? ['hashBrowns', 'eggs', 'bacon']
    : ['hashBrowns', 'waffle', 'eggs', 'bacon'];

  let count = rng.weighted(profile.items) + (bias.extraItems ?? 0);
  count = Math.max(1, Math.min(available.length, count));

  // Hash browns are the star of the show: almost every ticket has them.
  const kinds = new Set<ItemKind>();
  const hbWeight = bias.kinds?.hashBrowns ?? 1;
  if (hbWeight > 0 && rng.chance(Math.min(0.97, (count === 1 ? 0.68 : 0.88) * hbWeight))) {
    kinds.add('hashBrowns');
  }
  while (kinds.size < count) {
    const remaining = available.filter((k) => !kinds.has(k));
    const weighted = remaining.map(
      (k) => [k, BASE_KIND_WEIGHT[k] * (bias.kinds?.[k] ?? 1)] as const,
    );
    if (!weighted.some(([, w]) => w > 0)) break;
    kinds.add(rng.weighted(weighted));
  }
  if (kinds.size === 0) kinds.add('hashBrowns');

  const order: OrderSpec = {};
  if (kinds.has('hashBrowns')) {
    const allTheWay = rng.chance(profile.allTheWay);
    const toppingCount = allTheWay
      ? TOPPINGS.length
      : Math.max(0, Math.min(5, rng.weighted(profile.toppings) + (bias.extraToppings ?? 0)));
    order.hashBrowns = {
      toppings: pickToppings(rng, toppingCount),
      well: rng.chance(bias.wellChance ?? 0.2),
    };
  }
  if (kinds.has('waffle')) order.waffle = rng.chance(0.45) ? 'pecan' : 'classic';
  if (kinds.has('eggs')) order.eggs = rng.chance(0.6) ? 'scrambled' : 'overEasy';
  if (kinds.has('bacon')) order.bacon = true;
  return order;
}

/** Removes anything the kitchen can't make right now (used when the power drops). */
export function withoutWaffle(order: OrderSpec): OrderSpec {
  const { waffle: _waffle, ...rest } = order;
  if (!rest.hashBrowns && !rest.eggs && !rest.bacon) {
    return { ...rest, hashBrowns: { toppings: ['smothered', 'covered'], well: false } };
  }
  return rest;
}
